import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useIntl } from 'react-intl';
import { Form, Input } from 'antd';

import { IMyProfile } from '@share/common-types';
import { ILoginState, IMyProfileState, updateMyProfile } from '@share/store/slices';
import { FunctionalRootState, RootState } from '@share/utils';

import styles from './styles.module.scss';

interface IProfileFormValues {
  profile: IMyProfile;
}

/**
 * The member profile form in the hub layout: labels above fields, grouped in sections. Same field
 * names, rules and `updateMyProfile` call as the legacy `UpdateProfile`, so saving is unchanged.
 */
export const AccountProfileForm = (): React.ReactElement => {
  const intl = useIntl();
  const dispatch = useDispatch<ThunkDispatch<RootState, undefined, Action>>();
  const [form] = Form.useForm<IProfileFormValues>();

  const myProfileStore: IMyProfileState = useSelector((state: FunctionalRootState) => state.myProfileStore);
  const loginStore: ILoginState = useSelector((state: FunctionalRootState) => state.loginStore);

  const { loading, result } = myProfileStore;
  const isRSITemplate = !!loginStore.account?.isRSITemplate;

  const [submitted, setSubmitted] = useState<IProfileFormValues | null>(null);
  const [hasErrors, setHasErrors] = useState(false);

  useEffect(() => {
    form.setFieldsValue({ ...(submitted || result) } as IProfileFormValues);
  }, [result.profile, loading]);

  const required = { required: true, message: intl.formatMessage({ id: 'my.account.hub.form.required' }) };
  const label = (id: string) => intl.formatMessage({ id });

  const handleFinish = (values: IProfileFormValues) => {
    setSubmitted(values);
    dispatch(updateMyProfile(values.profile));
  };

  return (
    <Form
      form={form}
      layout="vertical"
      requiredMark={false}
      className={styles.hubForm}
      onFinish={handleFinish}
      onFieldsChange={() => setHasErrors(form.getFieldsError().some(field => field.errors.length > 0))}
    >
      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>{label('my.account.hub.profile.personal')}</legend>
        <div className={styles.gridName}>
          <Form.Item name={['profile', 'firstName']} label={label('first.name')} rules={[required]}>
            <Input autoComplete="given-name" />
          </Form.Item>
          <Form.Item name={['profile', 'middleName']} label={label('my.account.hub.profile.middle_initial')}>
            <Input autoComplete="additional-name" maxLength={3} />
          </Form.Item>
          <Form.Item name={['profile', 'lastName']} label={label('last.name')} rules={[required]}>
            <Input autoComplete="family-name" />
          </Form.Item>
        </div>
      </fieldset>

      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>{label('my.account.hub.profile.contact')}</legend>
        <div className={styles.grid2}>
          <Form.Item
            name={['profile', 'email']}
            label={label('email')}
            rules={[{ ...required, type: 'email' as const }]}
          >
            <Input type="email" autoComplete="email" />
          </Form.Item>
          {isRSITemplate ? (
            <Form.Item
              name={['profile', 'email2']}
              label={label('additional.email')}
              rules={[{ type: 'email' as const, message: intl.formatMessage({ id: 'my.account.hub.form.email' }) }]}
            >
              <Input type="email" autoComplete="off" />
            </Form.Item>
          ) : null}
          <Form.Item name={['profile', 'phone1']} label={label('cell.phone')} rules={[required]}>
            <Input type="tel" autoComplete="tel" />
          </Form.Item>
          <Form.Item name={['profile', 'phone2']} label={label('alt.phone')}>
            <Input type="tel" autoComplete="off" />
          </Form.Item>
        </div>
      </fieldset>

      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>{label('address')}</legend>
        <div className={styles.grid2}>
          <Form.Item name={['profile', 'address1']} label={label('my.account.hub.profile.address1')} rules={[required]}>
            <Input autoComplete="address-line1" />
          </Form.Item>
          <Form.Item name={['profile', 'address2']} label={label('my.account.hub.profile.address2')}>
            <Input autoComplete="address-line2" />
          </Form.Item>
        </div>
        <div className={styles.grid4}>
          <Form.Item name={['profile', 'city']} label={label('city')} rules={[required]}>
            <Input autoComplete="address-level2" />
          </Form.Item>
          <Form.Item name={['profile', 'state']} label={label('state')} rules={[required]}>
            <Input autoComplete="address-level1" />
          </Form.Item>
          <Form.Item name={['profile', 'postalCode']} label={label('postal-code')} rules={[required]}>
            <Input autoComplete="postal-code" />
          </Form.Item>
          <Form.Item name={['profile', 'country']} label={label('country')} rules={[required]}>
            <Input autoComplete="country-name" />
          </Form.Item>
        </div>
      </fieldset>

      <div className={styles.formActions}>
        <button type="submit" className={styles.primaryButton} disabled={loading || hasErrors}>
          {label('save.changes')}
        </button>
      </div>
    </Form>
  );
};
